/**
 * Le panneau d'ecole: maths, francais, et une calculatrice.
 *
 * Il sert DEUX endroits du monde, et c'est le seul parametre qui compte:
 *   - `'portable'`: le portable du bureau de la villa — maths et calculatrice;
 *   - `'ecole'`: le batiment de l'ecole — tout, maths et francais.
 *
 * Aucune regle n'est ecrite ici: tout vient de `shared/sim/ecole`, qui est du
 * code pur et teste sans navigateur. Ce fichier ne fait que trois choses:
 * dessiner des boutons, journaliser ce que l'enfant touche, et annoncer le
 * resultat par `onPartieFinie`.
 *
 * PAS DE `<input>`. Ni pour la calculatrice, ni pour les reponses: le clavier
 * du systeme mange la moitie d'un ecran de telephone, et le jeu se joue au
 * doigt. Tout est un vrai bouton d'au moins 44 px.
 */
import type { EtatCalc, ModeEcole, Niveau, Question, ReponseDonnee, ResultatPartie, Serie, Sujet, ToucheCalc } from '../../shared/sim/ecole/index.ts'
import {
  appuyer,
  composerResultat,
  etatCalcNeuf,
  genererSerie,
  nomNiveau,
  noterReponse,
  NOMBRE_DEFAUT,
  reponseAffichee,
  sujetInfo,
  sujetsDuMode,
  SUJETS,
} from '../../shared/sim/ecole/index.ts'
import { journal } from '../log/journal.ts'
import { bouton, clicSonore, el, Panneau, refusSonore, section } from './commun.ts'

/** Les niveaux, dans l'ordre du selecteur. */
const NIVEAUX_UI: readonly Niveau[] = [1, 2, 3, 4, 5, 6]

/** Duree d'affichage du verdict avant de passer a la suite, en mode chrono. */
const DUREE_VERDICT = 900

/** Periode de rafraichissement du chronometre, en ms. */
const PERIODE_CHRONO = 250

/** Une frappe repetee n'ecrit qu'une ligne de journal par tranche de ce temps. */
const LOG_MS = 1500

/** Le pave numerique, ligne par ligne. `null` = case vide. */
const TOUCHES_NOMBRE: readonly (readonly string[])[] = [
  ['7', '8', '9', '⌫'],
  ['4', '5', '6', ','],
  ['1', '2', '3', '/'],
  ['C', '0', 'valider'],
]

/** Le clavier de la calculatrice. */
const TOUCHES_CALC: readonly (readonly ToucheCalc[])[] = [
  ['C', 'signe', '%', '÷'],
  ['7', '8', '9', '×'],
  ['4', '5', '6', '−'],
  ['1', '2', '3', '+'],
  ['0', ',', 'retour', '='],
]

/** Ce qu'on ecrit sur la touche, quand ce n'est pas son identifiant. */
const ETIQUETTE_CALC: Record<string, string> = {
  signe: '±',
  retour: '⌫',
}

/** Le nom accessible d'une touche: un lecteur d'ecran ne lit pas « ⌫ ». */
const NOM_TOUCHE: Record<string, string> = {
  '⌫': 'Effacer le dernier chiffre',
  retour: 'Effacer le dernier chiffre',
  C: 'Tout effacer',
  ',': 'Virgule décimale',
  '/': 'Barre de fraction',
  valider: 'Valider ma réponse',
  signe: 'Changer le signe',
  '%': 'Pourcentage',
  '÷': 'Diviser',
  '×': 'Multiplier',
  '−': 'Soustraire',
  '+': 'Additionner',
  '=': 'Égale',
}

type Ecran = 'menu' | 'quiz' | 'bilan' | 'calculatrice'

export class PanneauEcole extends Panneau {
  /**
   * Une partie s'est terminee. C'est le SEUL point de sortie du score: le
   * panneau ne connait ni le serveur, ni la base, ni les recompenses.
   */
  onPartieFinie: ((r: ResultatPartie) => void) | null = null

  private mode: ModeEcole = 'ecole'
  private niveau: Niveau = 3
  private ecran: Ecran = 'menu'
  private chronoVoulu = false

  private serie: Serie | null = null
  private indexQuestion = 0
  private saisie = ''
  private reponses: ReponseDonnee[] = []
  private verdictAffiche = false
  private tQuestion = 0
  private tPartie = 0
  private finChrono = 0
  private partieEmise = false

  private calc: EtatCalc = etatCalcNeuf()

  private minuterieChrono: number | null = null
  private minuterieSuite: number | null = null
  /** Vrai quand la fermeture vient d'un choix de l'enfant, pas d'un abandon. */
  private fermetureParChoix = false
  private readonly derniersLogs = new Map<string, number>()

  /**
   * Les touches physiques, prises SUR LA BOITE et non sur la fenetre.
   *
   * On n'arrete la propagation que pour les touches qu'on consomme vraiment.
   * Echap et Tab doivent continuer leur route jusqu'a `Panneau`, qui les ecoute
   * sur la fenetre: les avaler ici aurait ferme la seule sortie du panneau.
   * Et les raccourcis du jeu (v, a, l, r) ecoutent eux aussi la fenetre — d'ou
   * l'arret de propagation des que la touche nous appartient.
   */
  private readonly surTouche = (e: KeyboardEvent): void => {
    if (e.ctrlKey || e.altKey || e.metaKey) return
    if (this.ecran === 'quiz') {
      const q = this.questionCourante()
      if (!q || q.saisie !== 'nombre') return
      if (this.verdictAffiche) {
        if (e.key !== 'Enter') return
        e.preventDefault()
        e.stopPropagation()
        this.questionSuivante()
        return
      }
      if ('0123456789'.includes(e.key)) this.frapper(e.key)
      else if (e.key === ',' || e.key === '.') this.frapper(',')
      else if (e.key === '/') this.frapper('/')
      else if (e.key === 'Backspace') this.frapper('⌫')
      else if (e.key === 'Enter') this.frapper('valider')
      else return
      e.preventDefault()
      e.stopPropagation()
      return
    }
    if (this.ecran === 'calculatrice') {
      const t = toucheCalcDe(e.key)
      if (!t) return
      this.frapperCalc(t)
      e.preventDefault()
      e.stopPropagation()
    }
  }

  constructor(parent: HTMLElement) {
    super(parent, 'ecole', 'École', '🎓', 'panneau-fond--ecole')
    this.boite.addEventListener('keydown', this.surTouche)
  }

  // ------------------------------------------------------------- ouverture

  /**
   * Ouvre le panneau. C'est l'API du monde: le portable appelle
   * `ouvrir('portable', niveau)`, l'ecole `ouvrir('ecole', niveau)`.
   */
  ouvrir(mode: ModeEcole = 'ecole', niveau: Niveau = 3): void {
    this.mode = mode
    this.niveau = NIVEAUX_UI.includes(niveau) ? niveau : 3
    this.serie = null
    this.reponses = []
    this.partieEmise = false
    this.arreterMinuteries()
    journal.input('ecole', `ouverture en mode ${mode}, niveau ${this.niveau}`)
    this.allerAuMenu(false)
    this.afficher(`${mode}, niveau ${this.niveau}`)
  }

  // ------------------------------------------------------------------ menu

  private allerAuMenu(journaliser = true): void {
    this.ecran = 'menu'
    this.arreterMinuteries()
    if (journaliser) journal.input('ecole', 'retour au menu')
    this.majEntete(this.mode === 'portable' ? 'Portable' : 'École', this.mode === 'portable' ? '💻' : '🎓')
    this.viderCorps()
    this.viderPied()

    // -------------------------------------------------------------- niveau
    const sNiveau = section('Année scolaire', '🎒')
    const segments = el('div', 'segments')
    for (const n of NIVEAUX_UI) {
      const b = el('button', 'segment', n === 1 ? '1re' : `${n}e`)
      b.type = 'button'
      b.setAttribute('aria-label', `Niveau ${nomNiveau(n)}`)
      b.setAttribute('aria-pressed', n === this.niveau ? 'true' : 'false')
      if (n === this.niveau) b.classList.add('segment--actif')
      b.addEventListener('click', () => {
        clicSonore()
        this.niveau = n
        journal.input('ecole', `niveau choisi: ${n}`)
        this.allerAuMenu(false)
      })
      segments.append(b)
    }
    sNiveau.append(segments)
    sNiveau.append(el('p', 'note', `Les exercices sont ceux de la ${nomNiveau(this.niveau)}.`))
    this.corps.append(sNiveau)

    // -------------------------------------------------------------- chrono
    const sDefi = section('Défi chronométré', '⏱️')
    const bChrono = el('button', 'bascule')
    bChrono.type = 'button'
    bChrono.textContent = this.chronoVoulu ? '⏱️ Chronomètre allumé' : '🐢 Sans chronomètre'
    bChrono.classList.toggle('bascule--active', this.chronoVoulu)
    bChrono.setAttribute('aria-pressed', this.chronoVoulu ? 'true' : 'false')
    bChrono.addEventListener('click', () => {
      clicSonore()
      this.chronoVoulu = !this.chronoVoulu
      journal.input('ecole', `chronometre ${this.chronoVoulu ? 'actif' : 'inactif'}`)
      this.allerAuMenu(false)
    })
    sDefi.append(bChrono)
    sDefi.append(
      el('p', 'note', 'Le chronomètre se pose par-dessus n’importe quel sujet, et rapporte plus de points.'),
    )
    this.corps.append(sDefi)

    // -------------------------------------------------------------- sujets
    const permis = sujetsDuMode(this.mode)
    const maths = SUJETS.filter((s) => s.matiere === 'maths' && permis.includes(s.id))
    const francais = SUJETS.filter((s) => s.matiere === 'francais' && permis.includes(s.id))

    const sMaths = section('Mathématiques', '🔢')
    sMaths.append(this.cartes(maths.map((s) => s.id)))
    this.corps.append(sMaths)

    if (francais.length > 0) {
      const sFrancais = section('Français', '📚')
      sFrancais.append(this.cartes(francais.map((s) => s.id)))
      this.corps.append(sFrancais)
    }

    // --------------------------------------------------------- calculatrice
    const sOutils = section('Outils', '🧮')
    sOutils.append(
      bouton(
        '🧮 Calculatrice',
        () => {
          journal.input('ecole', 'calculatrice ouverte')
          this.allerCalculatrice()
        },
        'bouton--sombre',
      ),
    )
    this.corps.append(sOutils)

    this.pied.append(
      el('span', 'pied__note', `${NOMBRE_DEFAUT} questions par partie. On ne perd jamais rien: on ne fait qu’apprendre.`),
    )
  }

  /** La grille de cartes d'un groupe de sujets. */
  private cartes(sujets: readonly Sujet[]): HTMLElement {
    const g = el('div', 'ecole-cartes')
    for (const id of sujets) {
      const info = sujetInfo(id)
      const b = el('button', 'ecole-carte')
      b.type = 'button'
      b.setAttribute('aria-label', `${info.nom} — ${info.resume}`)
      b.append(el('span', 'ecole-carte__icone', info.icone))
      const texte = el('span', 'ecole-carte__texte')
      texte.append(el('span', 'ecole-carte__nom', info.nom))
      texte.append(el('span', 'ecole-carte__resume', info.resume))
      b.append(texte)
      b.addEventListener('click', () => {
        clicSonore()
        this.demarrer([id])
      })
      g.append(b)
    }
    // Tout melanger: la meme mecanique, avec tous les sujets du groupe.
    if (sujets.length > 1) {
      const b = el('button', 'ecole-carte ecole-carte--melange')
      b.type = 'button'
      b.setAttribute('aria-label', 'Tout mélanger — un peu de chaque sujet')
      b.append(el('span', 'ecole-carte__icone', '🎲'))
      const texte = el('span', 'ecole-carte__texte')
      texte.append(el('span', 'ecole-carte__nom', 'Tout mélanger'))
      texte.append(el('span', 'ecole-carte__resume', 'Un peu de chaque sujet'))
      b.append(texte)
      b.addEventListener('click', () => {
        clicSonore()
        this.demarrer([...sujets])
      })
      g.append(b)
    }
    return g
  }

  // ------------------------------------------------------------------ quiz

  private demarrer(sujets: readonly Sujet[]): void {
    // La graine vient de l'horloge du CLIENT: shared/ n'a pas le droit de la
    // lire, c'est justement pour ca qu'elle arrive en argument.
    const graine = (Date.now() ^ (this.niveau * 7919)) >>> 0
    this.serie = genererSerie({
      niveau: this.niveau,
      sujets,
      nombre: NOMBRE_DEFAUT,
      graine,
      chrono: this.chronoVoulu,
    })
    this.indexQuestion = 0
    this.reponses = []
    this.saisie = ''
    this.verdictAffiche = false
    this.partieEmise = false
    this.tPartie = performance.now()
    journal.input('ecole', 'serie demarree', {
      mode: this.mode,
      niveau: this.niveau,
      sujets: [...sujets],
      graine,
      chrono: this.chronoVoulu,
      questions: this.serie.questions.length,
    })
    this.ecran = 'quiz'
    if (this.serie.secondes > 0) this.demarrerChrono(this.serie.secondes)
    this.dessinerQuestion()
  }

  private questionCourante(): Question | null {
    return this.serie?.questions[this.indexQuestion] ?? null
  }

  private dessinerQuestion(): void {
    const q = this.questionCourante()
    const serie = this.serie
    if (!q || !serie) {
      this.terminer('serie vide')
      return
    }
    this.saisie = ''
    this.verdictAffiche = false
    this.tQuestion = performance.now()
    journal.input('ecole', 'question posee', {
      n: this.indexQuestion + 1,
      id: q.id,
      activite: q.activite,
      enonce: q.enonce,
      attendue: q.reponses[0] ?? '',
    })

    this.majEntete(sujetInfo(q.sujet).nom, sujetInfo(q.sujet).icone)
    this.viderCorps()
    this.viderPied()

    this.corps.append(this.bandeau(serie))
    this.corps.append(el('p', 'ecole-enonce', q.enonce))
    this.corps.append(el('p', 'ecole-consigne', q.consigne))

    if (q.saisie === 'nombre') this.corps.append(this.zoneNombre(q))
    else this.corps.append(this.zoneChoix(q))

    this.pied.append(
      bouton(
        '🏳️ Abandonner',
        () => {
          journal.input('ecole', 'partie abandonnee par le bouton', {
            repondues: this.reponses.length,
            total: serie.questions.length,
          })
          this.arreterMinuteries()
          this.serie = null
          this.allerAuMenu()
        },
        'bouton--sombre',
      ),
    )
    this.corps.scrollTop = 0
  }

  /** Le bandeau du haut: avancement, score, chronometre. */
  private bandeau(serie: Serie): HTMLElement {
    const boite = el('div', 'ecole-bandeau')
    const total = serie.questions.length
    boite.append(el('span', 'ecole-bandeau__etape', `Question ${this.indexQuestion + 1} / ${total}`))
    const justes = this.reponses.filter((r) => r.juste).length
    boite.append(el('span', 'ecole-bandeau__score', `✅ ${justes}`))
    if (serie.secondes > 0) {
      const chrono = el('span', 'ecole-chrono', this.texteChrono())
      chrono.setAttribute('role', 'timer')
      boite.append(chrono)
    }
    const piste = el('div', 'ecole-jauge')
    const barre = el('div', 'ecole-jauge__barre')
    barre.style.width = `${Math.round((this.indexQuestion / total) * 100)}%`
    piste.append(barre)
    boite.append(piste)
    return boite
  }

  /** La zone de reponse chiffree: un afficheur et un pave de vrais boutons. */
  private zoneNombre(q: Question): HTMLElement {
    const boite = el('div', 'ecole-saisie')
    const ecran = el('div', 'ecole-ecran')
    ecran.setAttribute('role', 'status')
    ecran.setAttribute('aria-live', 'polite')
    ecran.append(el('span', 'ecole-ecran__valeur', this.saisie === '' ? '—' : this.saisie))
    if (q.unite) ecran.append(el('span', 'ecole-ecran__unite', q.unite))
    boite.append(ecran)

    const clavier = el('div', 'ecole-clavier')
    for (const ligne of TOUCHES_NOMBRE) {
      for (const t of ligne) {
        const b = el('button', t === 'valider' ? 'ecole-touche ecole-touche--valider' : 'ecole-touche')
        b.type = 'button'
        b.textContent = t === 'valider' ? '✔ Valider' : t
        b.setAttribute('aria-label', NOM_TOUCHE[t] ?? `Chiffre ${t}`)
        b.addEventListener('click', () => this.frapper(t))
        clavier.append(b)
      }
    }
    boite.append(clavier)
    return boite
  }

  /** La zone de reponse a choix: une pile de gros boutons. */
  private zoneChoix(q: Question): HTMLElement {
    const boite = el('div', 'ecole-choix')
    for (const option of q.choix ?? q.reponses) {
      const b = el('button', 'ecole-choix__bouton')
      b.type = 'button'
      b.textContent = option
      b.setAttribute('aria-label', `Réponse : ${option}`)
      b.addEventListener('click', () => {
        clicSonore()
        this.repondre(option)
      })
      boite.append(b)
    }
    return boite
  }

  /** Une touche du pave numerique. */
  private frapper(t: string): void {
    if (this.verdictAffiche) return
    const q = this.questionCourante()
    if (!q) return
    if (t === 'valider') {
      clicSonore()
      if (this.saisie === '') {
        refusSonore()
        journal.input('ecole', 'validation refusee: aucune reponse ecrite')
        return
      }
      this.repondre(this.saisie)
      return
    }
    clicSonore()
    if (t === 'C') this.saisie = ''
    else if (t === '⌫') this.saisie = this.saisie.slice(0, -1)
    else if (t === ',' && (this.saisie === '' || this.saisie.includes(','))) return
    else if (t === '/' && (this.saisie === '' || this.saisie.includes('/'))) return
    else if (this.saisie.length < 12) this.saisie += t
    else return
    this.logLimite('ecole', `saisie: ${this.saisie === '' ? 'vide' : this.saisie}`)
    this.majAfficheur()
  }

  private majAfficheur(): void {
    const valeur = this.corps.querySelector<HTMLElement>('.ecole-ecran__valeur')
    if (valeur) valeur.textContent = this.saisie === '' ? '—' : this.saisie
  }

  /** L'enfant repond: on corrige, on montre, on avance. */
  private repondre(donnee: string): void {
    const q = this.questionCourante()
    const serie = this.serie
    if (!q || !serie || this.verdictAffiche) return
    const ligne = noterReponse(q, donnee, performance.now() - this.tQuestion)
    this.reponses.push(ligne)
    this.verdictAffiche = true
    journal.input('ecole', ligne.juste ? 'bonne reponse' : 'mauvaise reponse', {
      n: this.indexQuestion + 1,
      id: q.id,
      donnee,
      attendue: ligne.attendue,
      ms: ligne.ms,
    })
    if (!ligne.juste) refusSonore()

    for (const b of this.corps.querySelectorAll<HTMLButtonElement>('.ecole-choix__bouton, .ecole-touche')) {
      b.disabled = true
    }

    const verdict = el('div', ligne.juste ? 'banniere banniere--succes' : 'banniere banniere--info')
    verdict.setAttribute('role', 'status')
    verdict.append(el('span', 'banniere__icone', ligne.juste ? '✅' : '💡'))
    verdict.append(
      el(
        'span',
        'banniere__texte',
        ligne.juste ? 'Bravo, c’est exact !' : `La bonne réponse était : ${reponseAffichee(q)}`,
      ),
    )
    this.corps.append(verdict)

    const suivant = bouton(
      this.indexQuestion + 1 >= serie.questions.length ? '🏁 Voir mon résultat' : 'Suivant ▶',
      () => this.questionSuivante(),
      'bouton--vert',
    )
    suivant.classList.add('ecole-suivant')
    this.corps.append(suivant)
    suivant.focus({ preventScroll: true })

    // En mode chrono, on n'attend pas: le temps court.
    if (serie.secondes > 0) {
      this.minuterieSuite = window.setTimeout(() => {
        this.minuterieSuite = null
        this.questionSuivante()
      }, DUREE_VERDICT)
    }
  }

  private questionSuivante(): void {
    if (this.minuterieSuite !== null) {
      window.clearTimeout(this.minuterieSuite)
      this.minuterieSuite = null
    }
    const serie = this.serie
    if (!serie) return
    this.indexQuestion++
    if (this.indexQuestion >= serie.questions.length) {
      this.terminer('serie complete')
      return
    }
    this.dessinerQuestion()
  }

  // --------------------------------------------------------------- chrono

  private demarrerChrono(secondes: number): void {
    this.finChrono = performance.now() + secondes * 1000
    this.minuterieChrono = window.setInterval(() => {
      const reste = this.finChrono - performance.now()
      const boite = this.corps.querySelector<HTMLElement>('.ecole-chrono')
      if (boite) {
        boite.textContent = this.texteChrono()
        boite.classList.toggle('ecole-chrono--urgent', reste < 10000)
      }
      if (reste <= 0) {
        journal.input('ecole', 'temps ecoule', { repondues: this.reponses.length })
        this.terminer('temps ecoule')
      }
    }, PERIODE_CHRONO)
  }

  private texteChrono(): string {
    const reste = Math.max(0, Math.ceil((this.finChrono - performance.now()) / 1000))
    const m = Math.floor(reste / 60)
    const s = reste % 60
    return `⏱️ ${m}:${String(s).padStart(2, '0')}`
  }

  // ---------------------------------------------------------------- bilan

  private terminer(raison: string): void {
    const serie = this.serie
    this.arreterMinuteries()
    if (!serie) {
      this.allerAuMenu()
      return
    }
    const resultat = composerResultat(this.mode, serie, this.reponses, performance.now() - this.tPartie)
    journal.input('ecole', `partie terminee (${raison})`, {
      justes: resultat.justes,
      total: resultat.total,
      pourcentage: resultat.pourcentage,
      etoiles: resultat.etoiles,
      points: resultat.points,
      meilleureSerie: resultat.meilleureSerie,
    })
    if (!this.partieEmise) {
      this.partieEmise = true
      this.onPartieFinie?.(resultat)
    }
    this.dessinerBilan(resultat)
  }

  private dessinerBilan(r: ResultatPartie): void {
    this.ecran = 'bilan'
    this.majEntete('Résultat', '🏆')
    this.viderCorps()
    this.viderPied()

    const bilan = el('div', 'ecole-bilan')
    bilan.append(el('div', 'ecole-etoiles', '★★★'.slice(0, r.etoiles) + '☆☆☆'.slice(0, 3 - r.etoiles)))
    bilan.append(el('div', 'ecole-bilan__score', `${r.justes} bonnes réponses sur ${r.total}`))
    bilan.append(el('div', 'ecole-bilan__detail', `${r.pourcentage} % · ${r.points} points gagnés`))
    if (r.meilleureSerie > 1) {
      bilan.append(el('div', 'ecole-bilan__detail', `Meilleure suite : ${r.meilleureSerie} d’affilée`))
    }
    this.corps.append(bilan)

    const rates = r.reponses.filter((x) => !x.juste)
    if (rates.length > 0) {
      const s = section('À revoir', '🔎')
      const liste = el('ul', 'ecole-recap')
      for (const x of rates) {
        const li = el('li', 'ecole-recap__ligne')
        li.append(el('span', 'ecole-recap__enonce', x.enonce))
        li.append(el('span', 'ecole-recap__bon', `Réponse : ${x.attendue}`))
        liste.append(li)
      }
      s.append(liste)
      this.corps.append(s)
    } else {
      this.corps.append(el('p', 'note', 'Sans faute ! On peut monter d’un niveau.'))
    }

    const serie = this.serie
    this.pied.append(
      bouton(
        '🔁 Rejouer',
        () => {
          journal.input('ecole', 'rejouer la meme sorte de serie')
          if (serie) this.demarrer(serie.options.sujets)
          else this.allerAuMenu()
        },
        'bouton--vert',
      ),
    )
    this.pied.append(bouton('📋 Menu', () => this.allerAuMenu(), 'bouton--sombre'))
    this.pied.append(
      bouton('✅ Terminer', () => {
        /*
         * Le resultat est DEJA parti (`terminer` l'a emis). Sans ce drapeau,
         * `surFermeture` prendrait cette fermeture voulue pour un abandon et
         * ecrirait une ligne « partie abandonnee » juste apres la ligne
         * « partie terminee »: le journal raconterait le contraire de ce qui
         * s'est passe. Meme motif que la construction.
         */
        this.fermetureParChoix = true
        journal.input('ecole', 'fermeture apres le bilan')
        this.fermer()
      }),
    )
  }

  // --------------------------------------------------------- calculatrice

  private allerCalculatrice(): void {
    this.ecran = 'calculatrice'
    this.arreterMinuteries()
    this.calc = etatCalcNeuf()
    this.dessinerCalculatrice()
  }

  private dessinerCalculatrice(): void {
    this.majEntete('Calculatrice', '🧮')
    this.viderCorps()
    this.viderPied()

    const ecran = el('div', 'ecole-calc__ecran')
    ecran.setAttribute('role', 'status')
    ecran.setAttribute('aria-live', 'polite')
    ecran.append(el('span', 'ecole-calc__rappel', this.calc.rappel))
    ecran.append(el('span', 'ecole-calc__valeur', this.calc.affichage))
    this.corps.append(ecran)

    const clavier = el('div', 'ecole-clavier ecole-clavier--calc')
    for (const ligne of TOUCHES_CALC) {
      for (const t of ligne) {
        const b = el('button', estOperation(t) ? 'ecole-touche ecole-touche--op' : 'ecole-touche')
        b.type = 'button'
        b.textContent = ETIQUETTE_CALC[t] ?? t
        b.setAttribute('aria-label', NOM_TOUCHE[t] ?? `Chiffre ${t}`)
        b.addEventListener('click', () => this.frapperCalc(t))
        clavier.append(b)
      }
    }
    this.corps.append(clavier)

    this.pied.append(bouton('📋 Menu', () => this.allerAuMenu(), 'bouton--sombre'))
  }

  private frapperCalc(t: ToucheCalc): void {
    clicSonore()
    this.calc = appuyer(this.calc, t)
    this.logLimite('ecole-calc', `calculatrice: ${this.calc.affichage}`)
    const valeur = this.corps.querySelector<HTMLElement>('.ecole-calc__valeur')
    const rappel = this.corps.querySelector<HTMLElement>('.ecole-calc__rappel')
    if (valeur) valeur.textContent = this.calc.affichage
    if (rappel) rappel.textContent = this.calc.rappel
  }

  // ----------------------------------------------------------------- vie

  private arreterMinuteries(): void {
    if (this.minuterieChrono !== null) window.clearInterval(this.minuterieChrono)
    if (this.minuterieSuite !== null) window.clearTimeout(this.minuterieSuite)
    this.minuterieChrono = null
    this.minuterieSuite = null
  }

  /** Une ligne de journal au plus par tranche, pour les gestes repetitifs. */
  private logLimite(tag: string, msg: string, tranche = LOG_MS): void {
    const t = performance.now()
    const precedent = this.derniersLogs.get(tag) ?? -Infinity
    if (t - precedent < tranche) return
    this.derniersLogs.set(tag, t)
    journal.input('ecole', msg)
  }

  protected override surFermeture(): void {
    this.arreterMinuteries()
    if (this.fermetureParChoix) {
      this.fermetureParChoix = false
      return
    }
    if (this.ecran === 'quiz' && this.serie && !this.partieEmise) {
      journal.input('ecole', 'partie abandonnee: panneau ferme', {
        repondues: this.reponses.length,
        total: this.serie.questions.length,
        justes: this.reponses.filter((r) => r.juste).length,
      })
    }
    this.serie = null
    this.ecran = 'menu'
  }

  override detruire(): void {
    this.arreterMinuteries()
    this.boite.removeEventListener('keydown', this.surTouche)
    this.onPartieFinie = null
    this.serie = null
    super.detruire()
  }
}

/** Vrai pour les touches qui teintent la colonne de droite de la calculatrice. */
function estOperation(t: ToucheCalc): boolean {
  return t === '+' || t === '−' || t === '×' || t === '÷' || t === '='
}

/** La touche de calculatrice qui correspond a une touche physique, s'il y en a une. */
function toucheCalcDe(cle: string): ToucheCalc | null {
  if ('0123456789'.includes(cle) && cle.length === 1) return cle as ToucheCalc
  if (cle === ',' || cle === '.') return ','
  if (cle === '+') return '+'
  if (cle === '-') return '−'
  if (cle === '*' || cle === 'x') return '×'
  if (cle === '/') return '÷'
  if (cle === 'Enter' || cle === '=') return '='
  if (cle === 'Backspace') return 'retour'
  if (cle === 'Delete') return 'C'
  if (cle === '%') return '%'
  return null
}
